/**
 * «Открытие точки» для менеджера УК (владелец 31.10.2026): чистая логика без React — фильтр списка в адресе
 * (`?status=`), выбранная карточка (`?card=`), ссылки туда и обратно, поиск по списку, подписи ячеек.
 * Покрыта opening-manager-model.test.ts. Вкладки и этап открытой карточки — в plan-model.ts (paramsForTab…).
 */
import type { OpeningPlanListItemDto, OpeningPlanListStatus } from '../../lib/api/openingTasks';
import { daysLabel, fmtDMY2, placeLabel } from './plan-model';

export const LIST_STATUSES: readonly OpeningPlanListStatus[] = ['active', 'opened', 'all'];

export const LIST_STATUS_LABEL: Record<OpeningPlanListStatus, string> = { active: 'В работе', opened: 'Открытые', all: 'Все' };

/** Фильтр по умолчанию — в адресе его нет. */
export const DEFAULT_LIST_STATUS: OpeningPlanListStatus = 'active';

/** Какой фильтр списка выбран по адресу; нет параметра или мусор — «В работе». */
export function statusFromParams(sp: URLSearchParams): OpeningPlanListStatus {
  const raw = sp.get('status');
  return LIST_STATUSES.find((s) => s === raw) ?? DEFAULT_LIST_STATUS;
}

/** Какая карточка открыта по адресу (`?card=`); пусто — список. */
export function cardIdFromParams(sp: URLSearchParams): string | null {
  const raw = sp.get('card')?.trim();
  return raw ? raw : null;
}

const BASE = '/opening';

/** Ссылка на список с фильтром: «В работе» — без параметра. */
export function listHref(status: OpeningPlanListStatus): string {
  return status === DEFAULT_LIST_STATUS ? BASE : `${BASE}?status=${status}`;
}

/** Ссылка на карточку из списка: фильтр списка сохраняется (по нему «← Все открытия» вернёт на тот же список). */
export function cardHref(id: string, status: OpeningPlanListStatus): string {
  const q = new URLSearchParams({ card: id });
  if (status !== DEFAULT_LIST_STATUS) q.set('status', status);
  return `${BASE}?${q.toString()}`;
}

/** Адрес списка после смены чипа статуса (`?status=` или пусто). */
export function paramsForListStatus(status: OpeningPlanListStatus): URLSearchParams {
  const next = new URLSearchParams();
  if (status !== DEFAULT_LIST_STATUS) next.set('status', status);
  return next;
}

// ───────────────────────── Поиск ─────────────────────────

/** Регистр, «ё», запятые и лишние пробелы — чтобы «вологда мира» нашло «Вологда, ул. Мира». */
export function normalizeSearch(s: string): string {
  return s.toLowerCase().replace(/ё/g, 'е').replace(/[,;]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Строка поиска карточки: ИП, город и адрес так, как они показаны (город один раз). */
export function searchHaystack(item: Pick<OpeningPlanListItemDto, 'title' | 'city' | 'address'>): string {
  return normalizeSearch(`${item.title} ${placeLabel(item.city, item.address)}`);
}

/** Поиск по ИП, городу и адресу: каждое слово запроса должно встретиться (в любом порядке). Пустой запрос — всё. */
export function filterOpenings<T extends Pick<OpeningPlanListItemDto, 'title' | 'city' | 'address'>>(items: readonly T[], query: string): T[] {
  const words = normalizeSearch(query).split(' ').filter(Boolean);
  if (words.length === 0) return [...items];
  return items.filter((it) => {
    const hay = searchHaystack(it);
    return words.every((w) => hay.includes(w));
  });
}

/** Что написать, когда показывать нечего. */
export function emptyListText(status: OpeningPlanListStatus, query: string): string {
  if (normalizeSearch(query)) return 'По запросу ничего не найдено — проверьте написание или смените фильтр.';
  if (status === 'active') return 'Открытий в работе сейчас нет.';
  if (status === 'opened') return 'Открытых точек пока нет.';
  return 'Открытий пока нет.';
}

// ───────────────────────── Подписи ячеек ─────────────────────────

/** Дни на этапе и норма по частям: «5 дней» и «из 10 по норме» (нормы нет — только дни). */
export function onStageParts(item: Pick<OpeningPlanListItemDto, 'daysOnStage' | 'normDays'>): { days: string; norm: string | null } {
  return { days: daysLabel(item.daysOnStage), norm: item.normDays != null ? `из ${item.normDays} по норме` : null };
}

/** «5 дней из 10 по норме» — как в «Свойствах» визарда; нормы нет — только дни. */
export function onStageText(item: Pick<OpeningPlanListItemDto, 'daysOnStage' | 'normDays'>): string {
  const { days, norm } = onStageParts(item);
  return norm ? `${days} ${norm}` : days;
}

/** «3/7» — задачи текущего этапа; задач нет — пусто. */
export function stageTasksText(item: Pick<OpeningPlanListItemDto, 'tasksDone' | 'tasksTotal'>): string {
  return item.tasksTotal > 0 ? `${item.tasksDone}/${item.tasksTotal}` : '';
}

/** Доля выполненных задач этапа — для сортировки колонки; задач нет — null. */
export function stageTasksRatio(item: Pick<OpeningPlanListItemDto, 'tasksDone' | 'tasksTotal'>): number | null {
  return item.tasksTotal > 0 ? item.tasksDone / item.tasksTotal : null;
}

/** «подписана 12.10.26» / «не подписана». */
export function leaseText(item: Pick<OpeningPlanListItemDto, 'leaseSignedAt'>): string {
  return item.leaseSignedAt ? `подписана ${fmtDMY2(item.leaseSignedAt)}` : 'не подписана';
}

/** Подсказка к тегу статуса «На паузе»: причина, если она есть. */
export function pauseHint(item: Pick<OpeningPlanListItemDto, 'statusV2' | 'pauseReason'>): string | undefined {
  return item.statusV2 === 'paused' && item.pauseReason ? `Причина паузы: ${item.pauseReason}` : undefined;
}
